import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { ProfileEntity, RelationEntity, UserStatusEntity } from '@interpals/api-web';

import api from 'api';
import { createCustomToast } from 'services/toasts';
import { ReduxState } from 'redux/root-reducer';

export const fetchProfile = createAsyncThunk<ProfileEntity, string, { rejectValue: Response }>(
    'profile/fetch',
    async (id, { rejectWithValue }) => {
        try {
            return await api.getProfileById({ id, extra: 'mutual_friends_count,relation' });
        } catch (e) {
            return rejectWithValue(e as Response);
        }
    },
);

export const blockUser = createAsyncThunk<RelationEntity, string>('profile/block', async (id) => {
    const response = await api.blockUserById({ id });
    createCustomToast('success', `${i18next.t('Toast.UserBlocked')}`, `${i18next.t('Common.Success')}`);

    return response;
});

export const unblockUser = createAsyncThunk<RelationEntity, string>('profile/unblock', async (id) => {
    const response = await api.unblockUserById({ id });
    createCustomToast('success', `${i18next.t('Toast.UserUnblocked')}`, `${i18next.t('Common.Success')}`);

    return response;
});

export type ReportEntityTypeName = 'group' | 'photo' | 'message' | 'post' | 'user' | 'comment';
export type ReportReasonTypeName = 'harass' | 'nudity' | 'other' | 'spam' | 'sex';

interface CreateReportParams {
    target_id: string;
    entity_type: ReportEntityTypeName;
    reason_sel: ReportReasonTypeName;
    target_uid?: string;
    reason?: string;
}

export const createReport = createAsyncThunk<Promise<void>, CreateReportParams>(
    'profile/report',
    async (params: CreateReportParams) => {
        const { target_id, entity_type, reason_sel, target_uid, reason } = params;
        await api.createReport({
            target_id,
            entity_type,
            reason_sel,
            target_uid,
            reason,
        });

        createCustomToast(
            'success',
            `${i18next.t('UserProfile.Report.SubmitMessage')}`,
            `${i18next.t('Common.Success')}`,
        );
    },
);

export const createBookmark = createAsyncThunk<Promise<void>, string>(
    'profile/bookmark/create',
    async (target_id: string) => {
        await api.createBookmark({ target_id });

        createCustomToast('success', `${i18next.t('UserProfile.Card.AddBookmark')}`, `${i18next.t('Common.Success')}`);
    },
);

export const deleteBookmark = createAsyncThunk<Promise<void>, string>(
    'profile/bookmark/delete',
    async (target_id: string) => {
        await api.deleteBookmark({ id: target_id });

        createCustomToast(
            'success',
            `${i18next.t('UserProfile.Card.DeleteBookmark')}`,
            `${i18next.t('Common.Success')}`,
        );
    },
);

export type RelationAction =
    | 'add_friend'
    | 'accept_friend'
    | 'ignore_friend'
    | 'delete_friend'
    | 'cancel_friend_request';

interface UpdateRelationParams {
    id: string;
    action: RelationAction;
}

export const updateRelation = createAsyncThunk<RelationEntity, UpdateRelationParams, { state: ReduxState }>(
    'profile/relation/update',
    async (params: UpdateRelationParams, { getState }) => {
        const { id, action } = params;
        const { profile } = getState();

        const response = await api.updateRelation({
            id,
            action,
        });

        (action === 'add_friend' || action === 'accept_friend') &&
            createCustomToast(
                'success',
                `${
                    (action === 'add_friend' && i18next.t('Toast.FriendRequestSent')) ||
                    (action === 'accept_friend' && i18next.t('Toast.AddedToFriends'))
                }`,
                `${i18next.t('Common.Success')}`,
            );

        // Hack. Should be handled somehow.
        if ((action === 'delete_friend' || action === 'cancel_friend_request') && profile) {
            return { ...profile.data?.relation, friend_status: 'none' } as RelationEntity;
        } else {
            return response;
        }
    },
);

export const updateUserStatus = createAsyncThunk<UserStatusEntity, { status: string }>(
    'profile/status/update',
    async ({ status }) => {
        return await api.updateUserStatus({ message: status });
    },
);

export const clearProfile = createAction('profile/clear');
